import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Archive, Search, FileText, RotateCcw, Calendar, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BibliothequeArchivesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchArchived(); }, []);

  const fetchArchived = async () => {
    try {
      const res = await fetch(`${API}/courses/?published_only=false`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Show unpublished courses as "archived" programmes
        setCourses(data.filter(c => !c.is_published));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handlePublish = async (courseId) => {
    try {
      const res = await fetch(`${API}/courses/${courseId}/publish`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        setCourses(prev => prev.filter(c => c.course_id !== courseId));
        toast.success('Programme republié');
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const filtered = courses.filter(c =>
    c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="bibliotheque-archives-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Archive className="h-6 w-6 text-slate-500" />
            Programmes archivés
          </h1>
          <p className="text-slate-500 mt-1">Programmes de formation non publiés ou archivés</p>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher un programme..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-archives"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-sm text-slate-600">Programmes archivés</p>
            <p className="text-2xl font-bold text-slate-900">{courses.length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-sm text-slate-600">En attente de publication</p>
            <p className="text-2xl font-bold text-amber-600">{courses.filter(c => !c.is_published).length}</p>
          </CardContent></Card>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun programme archivé</h3>
              <p className="text-slate-600">Tous les programmes sont actuellement publiés</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filtered.map(course => (
              <Card key={course.course_id} className="border-l-4 border-l-slate-300" data-testid={`archive-${course.course_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{course.title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge variant="outline" className="text-xs">{course.category}</Badge>
                          <span className="text-xs text-slate-500">{course.duration_hours}h</span>
                          <span className="text-xs text-slate-500">{course.level}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{course.description}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="border-teal-300 text-teal-600 hover:bg-teal-50"
                      onClick={() => handlePublish(course.course_id)}
                      data-testid={`republish-${course.course_id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Republier
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BibliothequeArchivesPage;
